/**
 * Reforma Tributária na Auditoria Fiscal (0.1.92, Doc. Técnica §70).
 *
 * - A regra de 2027 (fim da retenção de PIS/Cofins; só CSLL 1% no DARF 5987 + IRRF) tem um
 *   liga/desliga GLOBAL que só o Dono vê (GET/PUT /sistema/reforma-tributaria, 403 para os demais).
 * - O medidor da regra dos 80%: quanto da receita veio de cotas (estimativa pelo Resumo de
 *   Receitas dos balancetes). Abaixo de 80%, o condomínio passa a pagar IBS/CBS.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Landmark, Loader2, Power } from "lucide-react";
import { api } from "../lib/api";

export interface Indicador80 {
  pct_condominial: number; total: number; total_condominial: number;
  total_outra: number; total_financeira: number; meses: number;
}
interface Regra { ligada: boolean; atualizado_em: string | null; atualizado_por: string | null; vale_a_partir_de: string }

const brl = (n: number) => n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const dataHoraBR = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", dateStyle: "short", timeStyle: "short" }) : "";

export function Medidor80({ ind }: { ind: Indicador80 | null }) {
  if (!ind) {
    return (
      <p className="text-xs text-slate-500">
        Regra dos 80%: ainda sem o "Resumo de Receitas" nos balancetes deste período. Ele é lido nos
        balancetes processados a partir da 0.1.92 (reprocessar um mês também calcula).
      </p>
    );
  }
  const pct = Math.max(0, Math.min(100, ind.pct_condominial));
  const cor = pct < 80 ? "bg-rose-500" : pct < 85 ? "bg-amber-500" : "bg-emerald-500";
  const outras = ind.total_outra + ind.total_financeira;
  return (
    <div>
      <div className="flex items-baseline justify-between text-xs text-slate-600 mb-1">
        <span>Regra dos 80%: <b className="text-slate-800">{pct.toFixed(1).replace(".", ",")}%</b> da receita veio de cotas
          {ind.meses ? ` (${ind.meses} ${ind.meses === 1 ? "mês" : "meses"})` : ""}</span>
        <span className="text-slate-400">estimativa</span>
      </div>
      <div className="relative h-2.5 rounded-full bg-slate-100 overflow-hidden" role="meter"
        aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="Cotas sobre a receita total">
        <div className={`h-full ${cor}`} style={{ width: `${pct}%` }} />
        <div className="absolute top-0 bottom-0 w-0.5 bg-slate-700" style={{ left: "80%" }} title="limite de 80%" />
      </div>
      <p className="text-[11px] text-slate-500 mt-1">
        {pct < 80
          ? "Abaixo de 80%: pela LC 214/2025 o condomínio passa a pagar IBS/CBS. Confirme com o contador."
          : pct < 85
            ? "Perto do limite: acompanhe as receitas que não são cotas (aluguel, antena, aplicação)."
            : "Folga confortável: o condomínio segue sem IBS/CBS sobre as cotas."}
        {outras > 0 && ` Outras receitas no período: ${brl(outras)}.`}
      </p>
    </div>
  );
}

export function ReformaTributariaCard({ ligada, indicador, indevidas, aviso }: {
  ligada: boolean; indicador: Indicador80 | null; indevidas: number; aviso: string | null;
}) {
  const qc = useQueryClient();
  // Só o Dono recebe 200; para os demais (403) o botão simplesmente não aparece.
  const { data: regra } = useQuery<Regra>({
    queryKey: ["reforma-2027"],
    queryFn: () => api.get("/sistema/reforma-tributaria"),
    retry: false,
  });
  const trocar = useMutation({
    mutationFn: (l: boolean) => api.put<Regra>("/sistema/reforma-tributaria", { ligada: l }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reforma-2027"] });
      qc.invalidateQueries({ queryKey: ["auditoria-fiscal"] });
    },
  });
  const estado = regra ? regra.ligada : ligada;
  const pedir = () => {
    const msg = estado
      ? "Desligar a regra de 2027? As notas pagas a partir de 01/01/2027 voltam a ser conferidas pela regra antiga (4,65%)."
      : "Ligar a regra de 2027 para TODOS os condomínios?\n\nLigue só depois que o contador confirmar: a partir de 01/01/2027 (data do pagamento), não se retém mais PIS/Cofins, só a CSLL (1%, DARF 5987) e o IRRF.\n\nNada gravado é apagado; dá para desligar depois.";
    if (window.confirm(msg)) trocar.mutate(!estado);
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-4 mb-4">
      <div className="flex flex-wrap items-start gap-3">
        <Landmark size={22} className="text-slate-600 mt-0.5" />
        <div className="flex-1 min-w-[240px] space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-slate-800">Reforma Tributária</h3>
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${estado
              ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
              Regra de 2027: {estado ? "LIGADA" : "desligada"}
            </span>
          </div>
          <p className="text-xs text-slate-600">
            A partir de <b>01/01/2027</b> (data do pagamento), o condomínio não retém mais PIS/Cofins:
            fica só a <b>CSLL de 1%</b> (DARF <b>5987</b>) e o IRRF. ISS e INSS não mudam.
            {!estado && " Enquanto a regra estiver desligada, tudo é conferido como hoje."}
          </p>
          {regra?.atualizado_em && (
            <p className="text-[11px] text-slate-400">
              Última mudança: {dataHoraBR(regra.atualizado_em)} (horário de Brasília){regra.atualizado_por ? `, por ${regra.atualizado_por}` : ""}.
            </p>
          )}
        </div>
        {regra && (
          <button type="button" onClick={pedir} disabled={trocar.isPending}
            className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border disabled:opacity-50 ${estado
              ? "border-slate-300 text-slate-700 hover:bg-slate-50" : "border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700"}`}>
            {trocar.isPending ? <Loader2 size={15} className="animate-spin" /> : <Power size={15} />}
            {estado ? "Desligar a regra" : "Ligar a regra de 2027"}
          </button>
        )}
      </div>
      {indevidas > 0 && (
        <p className="mt-2 text-xs rounded-md bg-rose-50 border border-rose-200 text-rose-800 px-2.5 py-1.5">
          {indevidas} nota(s) paga(s) em 2027 ainda com PIS/Cofins retido: veja o alerta na coluna Federal.
        </p>
      )}
      {aviso && (
        <p className="mt-2 text-xs rounded-md bg-amber-50 border border-amber-200 text-amber-900 px-2.5 py-1.5">{aviso}</p>
      )}
      {trocar.isError && <p className="mt-2 text-xs text-rose-600">Não foi possível gravar. Tente de novo.</p>}
      <div className="mt-3 pt-3 border-t border-slate-100"><Medidor80 ind={indicador} /></div>
    </div>
  );
}
